import { DOCUMENT } from '@angular/common';
import { DestroyRef, EnvironmentProviders, inject, provideEnvironmentInitializer } from '@angular/core';
import {
  Event,
  NavigationCancel,
  NavigationCancellationCode,
  NavigationEnd,
  NavigationError,
  NavigationSkipped,
  Router,
} from '@angular/router';
import { first } from 'rxjs/operators';

// Matches the #launch-splash opacity transition in index.html.
export const LAUNCH_SPLASH_FADE_MS = 200;
// A first navigation that never settles must not leave the splash covering the app.
export const LAUNCH_SPLASH_MAX_MS = 10_000;

// A guard that redirects, or navigates imperatively, cancels the first navigation and
// starts another, so wait for that one.
function settlesInitialNavigation(event: Event): boolean {
  if (event instanceof NavigationCancel) {
    return (
      event.code !== NavigationCancellationCode.Redirect &&
      event.code !== NavigationCancellationCode.SupersededByNewNavigation
    );
  }
  return event instanceof NavigationEnd || event instanceof NavigationError || event instanceof NavigationSkipped;
}

// index.html shows #launch-splash in the native shells so their launch screens hand off
// without a blank frame. Fade it out once the first route has rendered.
export function provideLaunchSplashDismissal(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    const splash = inject(DOCUMENT).getElementById('launch-splash');
    if (!splash) return;

    let removeTimer: ReturnType<typeof setTimeout> | undefined;
    const dismiss = () => {
      if (removeTimer !== undefined) return;
      subscription.unsubscribe();
      clearTimeout(maxTimer);
      splash.classList.add('launch-splash-done');
      removeTimer = setTimeout(() => splash.remove(), LAUNCH_SPLASH_FADE_MS);
    };
    const maxTimer = setTimeout(dismiss, LAUNCH_SPLASH_MAX_MS);
    const subscription = inject(Router).events.pipe(first(settlesInitialNavigation)).subscribe(dismiss);

    inject(DestroyRef).onDestroy(() => {
      subscription.unsubscribe();
      clearTimeout(maxTimer);
      clearTimeout(removeTimer);
    });
  });
}
